import React, { useEffect, useState, useCallback } from "react";
import { Shield, Printer, MapPin, Phone, FileText, Check, X, ChevronRight, Eye, EyeOff } from "lucide-react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input, Select, Label } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { toast } from "../components/ui/Toast";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { formatDateTime, cn } from "../lib/utils";

const ROLE_KEYS = ["admin", "manager", "coordinator", "warehouse", "accountant", "shipper", "staff"];

// ─── Cấu hình chi tiết từng nhóm quyền ────────────────────────────────────────
const PERM_GROUPS = [
  {
    id: "orders",
    label: "Đơn hàng",
    icon: "🧾",
    color: "blue",
    perms: [
      { key: "orders.view",   label: "Xem",      desc: "Xem danh sách & chi tiết đơn hàng" },
      { key: "orders.create", label: "Thêm",     desc: "Tạo đơn hàng mới" },
      { key: "orders.edit",   label: "Sửa",      desc: "Cập nhật trạng thái, thông tin đơn" },
      { key: "orders.delete", label: "Xóa",      desc: "Xóa đơn hàng (PIN2 bảo vệ)" },
      { key: "orders.print",  label: "In",       desc: "In hóa đơn, phiếu giao hàng" },
    ],
  },
  {
    id: "products",
    label: "Sản phẩm",
    icon: "🥟",
    color: "green",
    perms: [
      { key: "products.view",   label: "Xem",  desc: "Xem danh mục sản phẩm" },
      { key: "products.create", label: "Thêm", desc: "Thêm sản phẩm mới" },
      { key: "products.edit",   label: "Sửa",  desc: "Chỉnh sửa thông tin sản phẩm" },
      { key: "products.delete", label: "Xóa",  desc: "Xóa sản phẩm" },
    ],
  },
  {
    id: "materials",
    label: "Nguyên vật liệu",
    icon: "🌾",
    color: "yellow",
    perms: [
      { key: "materials.view",   label: "Xem",  desc: "Xem danh sách nguyên liệu" },
      { key: "materials.create", label: "Thêm", desc: "Thêm nguyên liệu mới" },
      { key: "materials.edit",   label: "Sửa",  desc: "Chỉnh sửa nguyên liệu (PIN2)" },
      { key: "materials.delete", label: "Xóa",  desc: "Xóa nguyên liệu" },
    ],
  },
  {
    id: "stock",
    label: "Kho hàng",
    icon: "📦",
    color: "orange",
    perms: [
      { key: "stock.in",     label: "Nhập kho",   desc: "Nhập hàng, ghi nhận sản xuất" },
      { key: "stock.out",    label: "Xuất kho",   desc: "Xuất hàng thủ công" },
      { key: "stock.adjust", label: "Điều chỉnh", desc: "Điều chỉnh số lượng tồn kho" },
    ],
  },
  {
    id: "customers",
    label: "Khách hàng",
    icon: "👥",
    color: "purple",
    perms: [
      { key: "customers.view",   label: "Xem",      desc: "Xem danh sách khách hàng" },
      { key: "customers.create", label: "Thêm",     desc: "Thêm khách hàng mới" },
      { key: "customers.edit",   label: "Sửa",      desc: "Cập nhật thông tin khách" },
      { key: "customers.delete", label: "Xóa",      desc: "Xóa khách hàng" },
      { key: "customers.import", label: "Import",   desc: "Nhập khách hàng từ file Excel" },
    ],
  },
  {
    id: "suppliers",
    label: "Nhà cung cấp",
    icon: "🏭",
    color: "teal",
    perms: [
      { key: "suppliers.view",   label: "Xem",  desc: "Xem danh sách nhà cung cấp" },
      { key: "suppliers.create", label: "Thêm", desc: "Thêm nhà cung cấp mới" },
      { key: "suppliers.edit",   label: "Sửa",  desc: "Chỉnh sửa thông tin NCC" },
      { key: "suppliers.delete", label: "Xóa",  desc: "Xóa nhà cung cấp" },
    ],
  },
  {
    id: "debts",
    label: "Công nợ",
    icon: "💳",
    color: "red",
    perms: [
      { key: "debts.view",    label: "Xem",      desc: "Xem tổng quan công nợ khách & NCC" },
      { key: "debts.collect", label: "Thu tiền", desc: "Thu tiền nợ từ khách hàng" },
      { key: "debts.pay",     label: "Trả tiền", desc: "Trả nợ cho nhà cung cấp" },
      { key: "debts.remind",  label: "Nhắc nợ",  desc: "Gửi nhắc nhở thanh toán" },
    ],
  },
  {
    id: "delivery",
    label: "Giao hàng",
    icon: "🚚",
    color: "indigo",
    perms: [
      { key: "delivery.view",   label: "Xem",     desc: "Xem danh sách đơn giao hàng" },
      { key: "delivery.assign", label: "Phân công", desc: "Giao shipper cho đơn hàng" },
      { key: "delivery.bill",   label: "Xác nhận", desc: "Xác nhận giao thành công" },
    ],
  },
  {
    id: "reports",
    label: "Báo cáo",
    icon: "📊",
    color: "cyan",
    perms: [
      { key: "reports.view",   label: "Xem",     desc: "Xem báo cáo doanh thu, tồn kho, công nợ" },
      { key: "reports.export", label: "Xuất",    desc: "Xuất báo cáo ra file Excel/PDF" },
    ],
  },
  {
    id: "users",
    label: "Nhân viên",
    icon: "🔐",
    color: "gray",
    perms: [
      { key: "users.view",   label: "Xem",   desc: "Xem danh sách tài khoản nhân viên" },
      { key: "users.create", label: "Thêm",  desc: "Tạo tài khoản nhân viên mới" },
      { key: "users.edit",   label: "Sửa",   desc: "Chỉnh sửa thông tin, phân quyền nhân viên" },
      { key: "users.delete", label: "Xóa",   desc: "Xóa tài khoản nhân viên" },
    ],
  },
  {
    id: "settings",
    label: "Cài đặt",
    icon: "⚙️",
    color: "slate",
    perms: [
      { key: "settings.view", label: "Xem",  desc: "Xem cài đặt hệ thống" },
      { key: "settings.edit", label: "Sửa",  desc: "Thay đổi cài đặt hệ thống, cửa hàng" },
    ],
  },
];

const COLOR_MAP = {
  blue:   { bg: "bg-blue-50",   text: "text-blue-700",   border: "border-blue-200",   dot: "bg-blue-400"   },
  green:  { bg: "bg-green-50",  text: "text-green-700",  border: "border-green-200",  dot: "bg-green-400"  },
  yellow: { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200", dot: "bg-yellow-400" },
  orange: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", dot: "bg-orange-400" },
  purple: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200", dot: "bg-purple-400" },
  teal:   { bg: "bg-teal-50",   text: "text-teal-700",   border: "border-teal-200",   dot: "bg-teal-400"   },
  red:    { bg: "bg-red-50",    text: "text-red-700",    border: "border-red-200",    dot: "bg-red-400"    },
  indigo: { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-200", dot: "bg-indigo-400" },
  cyan:   { bg: "bg-cyan-50",   text: "text-cyan-700",   border: "border-cyan-200",   dot: "bg-cyan-400"   },
  gray:   { bg: "bg-gray-50",   text: "text-gray-700",   border: "border-gray-200",   dot: "bg-gray-400"   },
  slate:  { bg: "bg-slate-50",  text: "text-slate-700",  border: "border-slate-200",  dot: "bg-slate-400"  },
};

// ─── Toggle pill đơn lẻ ────────────────────────────────────────────────────────
function PermToggle({ enabled, disabled, onChange, label, desc }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onChange}
      title={desc}
      className={cn(
        "relative flex flex-col items-center gap-1 px-3 py-2 rounded-lg border-2 text-xs font-medium transition-all select-none",
        enabled
          ? "border-bamboo bg-bamboo/10 text-bamboo shadow-sm"
          : "border-border bg-white text-ink-muted hover:border-border/80",
        disabled && "opacity-40 cursor-not-allowed",
        !disabled && "cursor-pointer hover:shadow-sm"
      )}
    >
      <span className={cn(
        "w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors",
        enabled ? "bg-bamboo text-white" : "bg-gray-200 text-gray-400"
      )}>
        {enabled ? <Check size={11} strokeWidth={3} /> : <X size={10} strokeWidth={2.5} />}
      </span>
      <span className={cn("leading-tight text-center", enabled ? "text-bamboo font-semibold" : "text-ink-muted")}>
        {label}
      </span>
    </button>
  );
}

// ─── Modal phân quyền chi tiết ─────────────────────────────────────────────────
function PermissionModal({ open, onClose, user: targetUser, permSchema, onSaved }) {
  const [perms, setPerms] = useState([]);
  const [useCustom, setUseCustom] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);

  useEffect(() => {
    if (!targetUser || !permSchema) return;
    const customs = targetUser.permissions;
    const isCustom = Array.isArray(customs) && customs.length > 0;
    setUseCustom(isCustom);
    setPerms(isCustom ? customs : (permSchema.defaults?.[targetUser.role] || []));
    setActiveGroup(null);
  }, [targetUser, permSchema, open]);

  const toggle = (key) => {
    if (!useCustom) return;
    setPerms((prev) => prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]);
  };

  const toggleGroup = (groupPerms) => {
    if (!useCustom) return;
    const keys = groupPerms.map(p => p.key);
    const allOn = keys.every(k => perms.includes(k));
    if (allOn) setPerms(prev => prev.filter(k => !keys.includes(k)));
    else setPerms(prev => [...new Set([...prev, ...keys])]);
  };

  const applyPreset = (role) => {
    setPerms(permSchema?.defaults?.[role] || []);
    setUseCustom(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${targetUser.user_id}`, {
        permissions: useCustom ? perms : null,
      });
      toast({ title: "Đã lưu phân quyền", variant: "success" });
      onSaved();
      onClose();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Lỗi lưu phân quyền", variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (!targetUser) return null;

  const effectivePerms = useCustom ? perms : (permSchema?.defaults?.[targetUser.role] || []);
  const totalOn = effectivePerms.length;
  const totalAll = Object.values(PERM_GROUPS).flatMap(g => g.perms).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <Shield size={18} className="text-bamboo" />
          <span>Phân quyền — <span className="text-bamboo">{targetUser.name}</span></span>
        </div>
      }
      size="xl"
    >
      <div className="flex flex-col gap-0" style={{ minHeight: 520 }}>

        {/* Header strip */}
        <div className="flex items-center justify-between gap-4 pb-4 border-b border-border flex-wrap">
          <div className="flex items-center gap-3">
            <Badge variant={targetUser.role}>{targetUser.role}</Badge>
            <span className="text-xs text-ink-muted">
              {totalOn}/{totalAll} quyền được bật
            </span>
            <div className="h-2 w-32 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-bamboo rounded-full transition-all"
                style={{ width: `${Math.round((totalOn / totalAll) * 100)}%` }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Toggle custom */}
            <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
              <div
                onClick={() => setUseCustom(v => !v)}
                className={cn(
                  "relative w-9 h-5 rounded-full transition-colors cursor-pointer",
                  useCustom ? "bg-bamboo" : "bg-gray-300"
                )}
              >
                <div className={cn(
                  "absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all",
                  useCustom ? "left-4" : "left-0.5"
                )} />
              </div>
              <span className="font-medium">{useCustom ? "Tuỳ chỉnh" : "Dùng mặc định theo vai trò"}</span>
            </label>
          </div>
        </div>

        {/* Preset nhanh */}
        {useCustom && (
          <div className="py-3 border-b border-border/60">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-ink-muted font-medium">Nạp preset:</span>
              {ROLE_KEYS.map(r => (
                <button
                  key={r}
                  type="button"
                  onClick={() => applyPreset(r)}
                  className="text-xs px-2 py-1 rounded border border-border hover:bg-cream/60 transition-colors capitalize"
                >
                  {r}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPerms([])}
                className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
              >
                Xóa hết
              </button>
              <button
                type="button"
                onClick={() => setPerms(PERM_GROUPS.flatMap(g => g.perms.map(p => p.key)))}
                className="text-xs px-2 py-1 rounded border border-bamboo text-bamboo hover:bg-bamboo/10 transition-colors"
              >
                Bật tất cả
              </button>
            </div>
          </div>
        )}

        {/* Main layout: sidebar + detail */}
        <div className="flex gap-0 flex-1 min-h-0 mt-0" style={{ height: 380 }}>

          {/* Sidebar danh sách nhóm */}
          <div className="w-44 flex-shrink-0 overflow-y-auto border-r border-border py-2">
            {PERM_GROUPS.map((group) => {
              const c = COLOR_MAP[group.color];
              const groupKeys = group.perms.map(p => p.key);
              const onCount = groupKeys.filter(k => effectivePerms.includes(k)).length;
              const allOn = onCount === groupKeys.length;
              const someOn = onCount > 0 && !allOn;
              const isActive = activeGroup === group.id;

              return (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => setActiveGroup(isActive ? null : group.id)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2.5 text-left transition-colors text-sm",
                    isActive ? "bg-cream/80 font-semibold" : "hover:bg-cream/40"
                  )}
                >
                  <span className="text-base leading-none">{group.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{group.label}</div>
                    <div className="text-[10px] text-ink-muted">{onCount}/{groupKeys.length}</div>
                  </div>
                  <div className={cn(
                    "w-2 h-2 rounded-full flex-shrink-0",
                    allOn ? c.dot : someOn ? "bg-amber-400" : "bg-gray-200"
                  )} />
                  {isActive && <ChevronRight size={12} className="text-ink-muted -mr-1" />}
                </button>
              );
            })}
          </div>

          {/* Detail panel bên phải */}
          <div className="flex-1 overflow-y-auto py-3 px-4">
            {activeGroup === null ? (
              /* Overview: tất cả nhóm dạng compact */
              <div className="space-y-3">
                {PERM_GROUPS.map((group) => {
                  const c = COLOR_MAP[group.color];
                  const groupKeys = group.perms.map(p => p.key);
                  const onCount = groupKeys.filter(k => effectivePerms.includes(k)).length;
                  const allOn = onCount === groupKeys.length;

                  return (
                    <div key={group.id} className={cn("rounded-lg border p-3", c.border, c.bg)}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{group.icon}</span>
                          <span className={cn("text-xs font-semibold", c.text)}>{group.label}</span>
                          <span className="text-[10px] text-ink-muted">{onCount}/{groupKeys.length}</span>
                        </div>
                        {useCustom && (
                          <button
                            type="button"
                            onClick={() => toggleGroup(group.perms)}
                            className={cn(
                              "text-[10px] px-2 py-0.5 rounded border transition-colors",
                              allOn
                                ? "border-red-200 text-red-600 hover:bg-red-50"
                                : cn("border-current hover:opacity-80", c.text)
                            )}
                          >
                            {allOn ? "Tắt hết" : "Bật hết"}
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {group.perms.map((p) => {
                          const on = effectivePerms.includes(p.key);
                          return (
                            <PermToggle
                              key={p.key}
                              enabled={on}
                              disabled={!useCustom}
                              onChange={() => toggle(p.key)}
                              label={p.label}
                              desc={p.desc}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Detail 1 nhóm */
              (() => {
                const group = PERM_GROUPS.find(g => g.id === activeGroup);
                if (!group) return null;
                const c = COLOR_MAP[group.color];
                const groupKeys = group.perms.map(p => p.key);
                const allOn = groupKeys.every(k => effectivePerms.includes(k));

                return (
                  <div>
                    <div className={cn("flex items-center justify-between rounded-lg p-3 mb-4", c.bg, c.border, "border")}>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{group.icon}</span>
                        <div>
                          <div className={cn("font-heading font-bold", c.text)}>{group.label}</div>
                          <div className="text-xs text-ink-muted">
                            {groupKeys.filter(k => effectivePerms.includes(k)).length}/{groupKeys.length} quyền đang bật
                          </div>
                        </div>
                      </div>
                      {useCustom && (
                        <button
                          type="button"
                          onClick={() => toggleGroup(group.perms)}
                          className={cn(
                            "text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors",
                            allOn ? "border-red-200 text-red-600 hover:bg-red-50" : cn("border-current", c.text, "hover:opacity-80")
                          )}
                        >
                          {allOn ? "Tắt tất cả nhóm này" : "Bật tất cả nhóm này"}
                        </button>
                      )}
                    </div>

                    <div className="space-y-2">
                      {group.perms.map((p) => {
                        const on = effectivePerms.includes(p.key);
                        return (
                          <div
                            key={p.key}
                            onClick={() => useCustom && toggle(p.key)}
                            className={cn(
                              "flex items-center gap-4 p-3 rounded-lg border-2 transition-all",
                              on ? cn("border-bamboo bg-bamboo/5") : "border-border bg-white",
                              useCustom ? "cursor-pointer hover:shadow-sm" : "cursor-default"
                            )}
                          >
                            <div className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors",
                              on ? "bg-bamboo text-white" : "bg-gray-100 text-gray-400"
                            )}>
                              {on ? <Check size={16} strokeWidth={2.5} /> : <X size={14} />}
                            </div>
                            <div className="flex-1">
                              <div className={cn("font-medium text-sm", on ? "text-bamboo" : "text-ink-muted")}>
                                {p.label}
                              </div>
                              <div className="text-xs text-ink-muted mt-0.5">{p.desc}</div>
                              <div className="text-[10px] font-mono text-ink-muted/60 mt-0.5">{p.key}</div>
                            </div>
                            {/* Visual indicator */}
                            <div className={cn(
                              "text-xs font-semibold px-2 py-1 rounded",
                              on ? "bg-bamboo/10 text-bamboo" : "bg-gray-100 text-gray-400"
                            )}>
                              {on ? "BẬT" : "TẮT"}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 pt-4 border-t border-border mt-auto">
          <div className="text-xs text-ink-muted">
            {useCustom
              ? `Tuỳ chỉnh: ${perms.length} quyền được chọn`
              : `Dùng mặc định vai trò "${targetUser.role}" — ${effectivePerms.length} quyền`}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" type="button" onClick={onClose}>Huỷ</Button>
            <Button type="button" onClick={save} disabled={saving}>
              <Shield size={14} />
              {saving ? "Đang lưu..." : "Lưu phân quyền"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function Users() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [permSchema, setPermSchema] = useState(null);

  // Modals
  const [editOpen, setEditOpen] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "staff", phone: "", payment_method: "", is_active: true });
  const [showUserPw, setShowUserPw] = useState(false);

  // Phân quyền modal
  const [permTarget, setPermTarget] = useState(null);
  const [permOpen, setPermOpen] = useState(false);

  // Modal chi tiết nhân viên
  const [detailTarget, setDetailTarget] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailForm, setDetailForm] = useState({ name: "", phone: "", address: "", note: "", payment_method: "" });
  const [detailSaving, setDetailSaving] = useState(false);

  const openDetail = (u) => {
    setDetailTarget(u);
    setDetailForm({
      name: u.name || "",
      phone: u.phone || "",
      address: u.address || "",
      note: u.note || "",
      payment_method: u.payment_method || "",
    });
    setDetailOpen(true);
  };

  const saveDetail = async () => {
    setDetailSaving(true);
    try {
      await api.put(`/users/${detailTarget.user_id}`, {
        name: detailForm.name,
        phone: detailForm.phone,
        address: detailForm.address,
        note: detailForm.note,
        payment_method: detailForm.payment_method,
      });
      toast({ title: "Đã lưu thông tin", variant: "success" });
      setDetailOpen(false);
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Lỗi lưu", variant: "error" });
    } finally { setDetailSaving(false); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/users");
    setItems(data);
  }, []);

  useEffect(() => {
    load();
    api.get("/system/permissions").then((r) => setPermSchema(r.data)).catch(() => {});
  }, []);

  const openCreate = () => {
    setCreateMode(true);
    setEditing(null);
    setForm({ email: "", name: "", password: "", role: "staff", phone: "", payment_method: "", is_active: true });
    setEditOpen(true);
  };

  const openEdit = (u) => {
    setCreateMode(false);
    setEditing(u);
    setForm({
      email: u.email, name: u.name, password: "",
      role: u.role, phone: u.phone || "",
      payment_method: u.payment_method || "",
      is_active: u.is_active !== false,
    });
    setEditOpen(true);
  };

  const openPerm = (u) => {
    setPermTarget(u);
    setPermOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (createMode) {
        // Admin chỉ tạo "hồ sơ" nhân viên (tên + role), không có email/mk
        // Email + mật khẩu sẽ do Creator Panel gán sau
        await api.post("/users", {
          name: form.name,
          role: form.role,
          phone: form.phone,
        });
      } else {
        const body = {
          name: form.name, role: form.role, phone: form.phone,
          payment_method: form.payment_method, is_active: form.is_active,
        };
        if (form.password) body.password = form.password;
        await api.put(`/users/${editing.user_id}`, body);
      }
      toast({ title: t("common.saved"), variant: "success" });
      setEditOpen(false);
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || t("common.error"), variant: "error" });
    }
  };

  const remove = async (u) => {
    if (u.user_id === user?.user_id) return;
    if (!window.confirm(`${t("common.delete")} "${u.name}"?`)) return;
    try {
      await api.delete(`/users/${u.user_id}`);
      toast({ title: t("common.deleted"), variant: "success" });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || t("common.error"), variant: "error" });
    }
  };

  const handlePrint = () => {
    const ROLES = { admin: "Admin", manager: "Quản lý", coordinator: "Điều phối", warehouse: "Kho", accountant: "Kế toán", shipper: "Shipper", staff: "Nhân viên" };
    const rows = items.map((u, i) => `<tr>
      <td>${i + 1}</td><td>${u.name || ""}</td><td>${u.email || ""}</td>
      <td>${ROLES[u.role] || u.role || ""}</td>
      <td>${u.is_active === false ? "Ngừng" : "Đang hoạt động"}</td>
      <td>${u.created_at ? u.created_at.slice(0, 10) : ""}</td>
    </tr>`).join("");
    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html><html lang="vi"><head><meta charset="UTF-8"/>
    <title>Danh sách người dùng</title>
    <style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px}h2{margin:0 0 4px}p.sub{color:#555;font-size:11px;margin:0 0 12px}
    table{width:100%;border-collapse:collapse}th,td{border:1px solid #ccc;padding:5px 8px;text-align:left}
    th{background:#f0f0f0;font-size:11px;text-transform:uppercase}tr:nth-child(even) td{background:#fafafa}
    @media print{body{margin:10mm}}</style></head><body>
    <h2>Danh sách người dùng</h2>
    <p class="sub">${items.length} tài khoản</p>
    <table><thead><tr><th>STT</th><th>Họ tên</th><th>Email</th><th>Vai trò</th><th>Trạng thái</th><th>Ngày tạo</th></tr></thead>
    <tbody>${rows}</tbody></table>
    <script>window.onload=()=>{window.print()}<\/script></body></html>`);
    win.document.close();
  };

  // Hiển thị tóm tắt quyền của user
  const getPermSummary = (u) => {
    const customs = u.permissions;
    if (Array.isArray(customs) && customs.length > 0) {
      return { count: customs.length, custom: true };
    }
    const defaults = permSchema?.defaults?.[u.role] || [];
    return { count: defaults.length, custom: false };
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t("users.title")}</h1>
          <p className="text-sm text-ink-secondary mt-1">{items.length} {t("common.total").toLowerCase()}</p>
        </div>
        {user?.role === "admin" && (
          <Button variant="outline" onClick={handlePrint}>
            <Printer size={14} /> In
          </Button>
        )}
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
              <tr>
                <th className="text-left py-3 px-4">{t("common.name")}</th>
                <th className="text-left py-3 px-4">SĐT</th>
                <th className="text-center py-3 px-4">{t("users.role")}</th>
                <th className="text-center py-3 px-4">Phân quyền</th>
                <th className="text-center py-3 px-4">{t("common.status")}</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody data-testid="users-table-body">
              {items.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-12 text-ink-muted">{t("common.empty")}</td></tr>
              ) : items.map((u) => {
                const perm = permSchema ? getPermSummary(u) : null;
                const isSelf = u.user_id === user?.user_id;
                return (
                  <tr key={u.user_id} className="border-t border-border hover:bg-cream/30" data-testid={`user-row-${u.user_id}`}>
                    <td className="py-3 px-4 font-medium">{u.name}</td>
                    <td className="py-3 px-4 text-xs text-ink-muted">{u.phone || "—"}</td>
                    <td className="py-3 px-4 text-center">
                      <Badge variant={u.role}>{t(`users.${u.role}`)}</Badge>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {perm ? (
                        <button
                          type="button"
                          onClick={() => user?.role === "admin" && openPerm(u)}
                          className={cn(
                            "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors",
                            perm.custom
                              ? "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100"
                              : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100",
                            user?.role !== "admin" && "cursor-default pointer-events-none"
                          )}
                        >
                          <Shield size={11} />
                          <span>{perm.count} quyền</span>
                          {perm.custom && <span className="text-[10px] opacity-70">(tuỳ chỉnh)</span>}
                        </button>
                      ) : (
                        <span className="text-xs text-ink-muted">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {u.is_active !== false
                        ? <Badge variant="delivered">{t("users.active")}</Badge>
                        : <Badge variant="cancelled">{t("users.inactive")}</Badge>}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {user?.role === "admin" && (
                        <div className="flex justify-end items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDetail(u)}
                            className="text-ink-secondary hover:bg-cream-dark gap-1 text-xs"
                            title="Xem & sửa thông tin nhân viên"
                          >
                            <FileText size={13} /> Chi tiết
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPerm(u)}
                            className="text-bamboo hover:bg-bamboo/10 gap-1 text-xs"
                            title="Phân quyền chi tiết"
                            data-testid={`user-perm-${u.user_id}`}
                          >
                            <Shield size={13} /> Phân quyền
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ── Modal thêm/sửa thông tin cơ bản ── */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title={createMode ? t("common.add") : t("common.edit")} size="lg" testId="user-modal">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{t("common.name")} *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-form-name" />
            </div>
            {/* Email: chỉ hiện khi chỉnh sửa, không cho nhập khi tạo mới */}
            {!createMode && (
              <div>
                <Label>{t("common.email")}</Label>
                <Input type="email" value={form.email} disabled data-testid="user-form-email" />
              </div>
            )}
            {/* Mật khẩu: chỉ hiện khi chỉnh sửa (đổi mật khẩu), không cho tạo khi thêm mới */}
            {!createMode && (
              <div>
                <Label>{t("users.newPassword")} <span className="text-ink-muted font-normal">(để trống = không đổi)</span></Label>
                <div className="relative">
                  <Input type={showUserPw ? "text" : "password"} value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="pr-9" data-testid="user-form-password" />
                  <button type="button" onClick={() => setShowUserPw(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition-colors" tabIndex={-1}>
                    {showUserPw ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
            )}
            <div>
              <Label>{t("common.phone")}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label>{t("users.role")}</Label>
              <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-form-role">
                {ROLE_KEYS.map((r) => <option key={r} value={r}>{t(`users.${r}`)}</option>)}
              </Select>
            </div>
            {!createMode && (
              <div>
                <Label>{t("users.paymentMethod")}</Label>
                <Input value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} placeholder="Tiền mặt / Chuyển khoản" />
              </div>
            )}
            {!createMode && (
              <div className="sm:col-span-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="user-form-active" />
                  {t("users.active")}
                </label>
              </div>
            )}
          </div>
          {/* Thông báo khi tạo mới: nhắc Creator gán email + mật khẩu */}
          {createMode && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1">
              <p className="text-xs font-semibold text-amber-700">⚠️ Nhân viên chưa có tài khoản đăng nhập</p>
              <p className="text-xs text-amber-600">
                Email và mật khẩu sẽ do <strong>Creator</strong> gán riêng. Sau khi tạo, vào{" "}
                <Shield size={11} className="inline" /> <strong>Phân quyền</strong> để cấu hình quyền chi tiết.
              </p>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => setEditOpen(false)}>{t("common.cancel")}</Button>
            <Button type="submit" data-testid="user-form-submit">{t("common.save")}</Button>
          </div>
        </form>
      </Modal>

      {/* ── Modal chi tiết nhân viên ── */}
      <Modal open={detailOpen} onClose={() => setDetailOpen(false)} title="Thông tin nhân viên" size="md">
        {detailTarget && (
          <div className="space-y-4">
            {/* Header nhân viên */}
            <div className="flex items-center gap-3 pb-3 border-b border-border">
              <div className="w-10 h-10 rounded-full bg-bamboo/10 flex items-center justify-center text-bamboo font-bold text-lg">
                {detailTarget.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <div className="font-semibold text-ink">{detailTarget.name}</div>
                <Badge variant={detailTarget.role} className="text-[10px]">{t(`users.${detailTarget.role}`)}</Badge>
              </div>
            </div>

            {/* Form chỉnh sửa */}
            <div className="space-y-3">
              <div>
                <Label>Họ tên</Label>
                <Input
                  value={detailForm.name}
                  onChange={e => setDetailForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Nguyễn Văn A"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label><Phone size={11} className="inline mr-1" />Số điện thoại</Label>
                  <Input
                    value={detailForm.phone}
                    onChange={e => setDetailForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="0901234567"
                  />
                </div>
                <div>
                  <Label>Hình thức trả lương</Label>
                  <Input
                    value={detailForm.payment_method}
                    onChange={e => setDetailForm(f => ({ ...f, payment_method: e.target.value }))}
                    placeholder="Tiền mặt / Chuyển khoản"
                  />
                </div>
              </div>
              <div>
                <Label><MapPin size={11} className="inline mr-1" />Địa chỉ</Label>
                <Input
                  value={detailForm.address}
                  onChange={e => setDetailForm(f => ({ ...f, address: e.target.value }))}
                  placeholder="Số nhà, đường, phường/xã, quận/huyện..."
                />
              </div>
              <div>
                <Label><FileText size={11} className="inline mr-1" />Ghi chú</Label>
                <textarea
                  value={detailForm.note}
                  onChange={e => setDetailForm(f => ({ ...f, note: e.target.value }))}
                  placeholder="Ghi chú nội bộ về nhân viên..."
                  rows={3}
                  className="w-full text-sm px-3 py-2 rounded-md border border-border bg-white focus:outline-none focus:ring-2 focus:ring-bamboo/40 resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setDetailOpen(false)}>Đóng</Button>
              <Button onClick={saveDetail} disabled={detailSaving}>
                {detailSaving ? "Đang lưu..." : "💾 Lưu"}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Modal phân quyền chi tiết ── */}
      <PermissionModal
        open={permOpen}
        onClose={() => setPermOpen(false)}
        user={permTarget}
        permSchema={permSchema}
        onSaved={load}
      />
    </div>
  );
}